import {
  APP_ID,
  Component,
  HostListener,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { Message } from './message';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { UserService } from './shared/services/user.service';
import { LoadingService } from './shared/services/loading.service';
import { BroadcastService } from './shared/services/broadcast.service';
// import AnalyticsSdk from './shared/js/sdk.analytics_v_3.js';

declare var webengage: any;

const WE_DEEPLINK_OPENED_SESSION_KEY = 'we_deeplink_opened_session';
const WE_SITE_VISITED_BEFORE_KEY = 'we_site_visited_before';
const WE_ONBOARDING_STARTED_AT_KEY = 'we_onboarding_started_at';
const WE_ONBOARDING_COMPLETED_KEY = 'we_onboarding_completed';
const WE_FIRST_VISIT_TS_KEY = 'we_first_visit_ts';
const WE_NOTIF_GRANTED_TRACKED_KEY = 'we_notif_permission_granted_tracked';
const WE_NOTIF_DENIED_TRACKED_KEY = 'we_notif_permission_denied_tracked';

function mapDeeplinkTarget(pathname: string): {
  target_screen: string;
  target_id: string;
} {
  const segs = (pathname || '/').replace(/^\//, '').split('/').filter(Boolean);
  const n = segs.length;
  if (n === 0) return { target_screen: 'homepage', target_id: '' };

  const first = segs[0];
  const last = segs[n - 1];

  if (last === 'play') {
    return {
      target_screen: n >= 4 ? 'episode_play' : 'title_play',
      target_id: segs[n - 2] || '',
    };
  }
  // See All listing pages (/all, /episodes) — before n===2/n===3 details mapping
  if (last === 'all' || last === 'episodes') {
    return {
      target_screen: 'listing',
      target_id: segs[n - 2] || segs[0] || '',
    };
  }
  if (first === 'starrer' && segs[1]) {
    return { target_screen: 'star_detail', target_id: segs[1] };
  }
  if (first === 'plans') {
    return { target_screen: 'plans', target_id: '' };
  }
  if (first === 'live-tv') {
    return { target_screen: 'live_tv', target_id: '' };
  }
  if (first === 'shorts') {
    return { target_screen: 'shorts', target_id: '' };
  }
  if (first === 'users' && segs[1]) {
    return {
      target_screen: `user_${segs[1].replace(/-/g, '_')}`,
      target_id: segs[2] || '',
    };
  }
  if (first === 'payments') {
    return { target_screen: 'payment_result', target_id: last || '' };
  }
  if (n === 2) {
    return { target_screen: 'title_detail', target_id: segs[1] };
  }
  if (n === 3) {
    return { target_screen: 'episode_detail', target_id: segs[2] };
  }
  if (n === 1) {
    const knownListRoutes: Record<string, string> = {
      'serial-list': 'serial_list',
      'web-series-list': 'web_series_list',
      'shorts-list': 'shorts_list',
      'movie-list': 'movie_list',
      'shows-list': 'shows_list',
      'all-channels': 'live_tv_list',
    };
    if (knownListRoutes[first]) {
      return { target_screen: knownListRoutes[first], target_id: '' };
    }
    return { target_screen: 'catalog_list', target_id: segs[0] };
  }
  return { target_screen: 'other', target_id: segs.join('/') };
}

declare global {
  interface Window {
    googletag: any;
    sdk: any;
  }
}
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  browser: string = 'web';
  /** Controls header/footer visibility; false only on Android in-app routes. */
  showLayout: boolean = true;
  title = 'Dangal Play';
  showSplash: boolean = true;
  firstTimeLoad: boolean = true;
  private deeplinkOpenedFlush: ReturnType<typeof setTimeout> | null = null;
  isServer: any;
  isBrowser: any;
  headers = new HttpHeaders()
    .set('content-type', 'application/json')
    .set('charset', 'utf-8');
  options = { headers: this.headers };
  region: string = '';
  isRegionSupported: boolean = true;
  disableSplash = false;
  private readonly landingPathname: string = window.location.pathname || '/';
  private readonly landingSearch: string = window.location.search || '';

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private userService: UserService,
    private loadingService: LoadingService,
    private broadcastService: BroadcastService,
    @Inject(PLATFORM_ID) private platformId: Object,
    // @Inject(APP_ID) private appId: string,
  ) {
    const params = new URLSearchParams(window.location.search);
    this.browser = params.get('browser') || 'web';
    this.region = params.get('region') || '';
    console.log('Browser detected from query param:', this.browser);
    try {
      if (isPlatformBrowser(this.platformId)) {
        const hasStart = window.localStorage?.getItem(
          WE_ONBOARDING_STARTED_AT_KEY,
        );
        if (!hasStart) {
          window.localStorage?.setItem(
            WE_ONBOARDING_STARTED_AT_KEY,
            String(Date.now()),
          );
        }
        const fv = window.localStorage?.getItem(WE_FIRST_VISIT_TS_KEY);
        if (!fv) {
          window.localStorage?.setItem(
            WE_FIRST_VISIT_TS_KEY,
            String(Date.now()),
          );
        }
      }
    } catch {
      /* non-fatal */
    }
    if (this.browser === 'android') {
      this.disableSplash = true;
      this.showSplash = false;
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncBrowserFromRoute(event.urlAfterRedirects);

        if (isPlatformBrowser(this.platformId)) {
          this.trackDeeplinkOpened();
        }

        // Android / iOS / mobile → NO splash EVER
        if (['android', 'ios', 'mobile'].includes(this.browser)) {
          this.showSplash = false;
          return;
        }
        // Web only → splash on first home load
        if (this.firstTimeLoad && event.urlAfterRedirects === '/') {
          this.firstTimeLoad = false;
          this.showSplash = true;

          setTimeout(() => {
            this.showSplash = false;
          }, 3000);
        } else {
          this.showSplash = false;
        }
      }
    });
  }
  // Disable right click
  @HostListener('contextmenu', ['$event'])
  onRightClick(event) {
    event.preventDefault();
  }

  ngOnInit(): void {
    this.syncBrowserFromRoute(this.router.url || window.location.pathname);

    this.httpClient
      .get('.well-known/assetlinks.json', this.options)
      .subscribe((data) => {});
    this.isServer = isPlatformServer(this.platformId);
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.initNotificationPermissionListener();
    }
  }

  ngAfterViewInit() {
    console.log('App Component - ngAfterViewInit');
  }

  /**
   * Hide chrome only on Android in-app routes. Stale localStorage must not
   * suppress header/footer on the normal website.
   */
  private isAndroidLayoutRoute(url: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    try {
      const normalized = url.startsWith('http')
        ? url
        : `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
      const parsed = new URL(normalized);
      const path = parsed.pathname.replace(/\/$/, '') || '/';

      if (parsed.searchParams.get('browser') === 'android') {
        return true;
      }
      if (path === '/app-plans' || path.startsWith('/app-plans/')) {
        return true;
      }

      const androidSessionRoutes = [
        '/plans',
        '/payments/payment_success',
        '/payments/payment_failure',
      ];
      const onAndroidSessionRoute = androidSessionRoutes.some(
        (route) => path === route || path.startsWith(`${route}/`),
      );
      if (
        onAndroidSessionRoute &&
        window.localStorage?.getItem('browser') === 'android'
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private syncBrowserFromRoute(url: string): void {
    if (this.isAndroidLayoutRoute(url)) {
      this.browser = 'android';
      this.showLayout = false;
      return;
    }

    const query = url.includes('?') ? url.split('?')[1] : window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(query);
    this.browser = params.get('browser') || 'web';
    this.showLayout = true;
    this.restoreHeaderFooter();
  }

  private restoreHeaderFooter(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      document.querySelectorAll('app-header, app-footer').forEach((el) => {
        (el as HTMLElement).style.display = '';
      });
    } catch {
      /* non-fatal */
    }
  }

  /**
   * WebEngage `Deeplink Opened` (Acquisition & Onboarding).
   * Fires once per tab-session after landing URL stabilizes.
   */
  private trackDeeplinkOpened(): void {
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (sessionStorage.getItem(WE_DEEPLINK_OPENED_SESSION_KEY)) return;

      if (this.deeplinkOpenedFlush) clearTimeout(this.deeplinkOpenedFlush);
      this.deeplinkOpenedFlush = setTimeout(() => {
        this.deeplinkOpenedFlush = null;
        this.flushDeeplinkOpened();
      }, 250);
    } catch {
      /* non-fatal */
    }
  }

  private flushDeeplinkOpened(): void {
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (sessionStorage.getItem(WE_DEEPLINK_OPENED_SESSION_KEY)) return;

      const currentPathname = window.location.pathname || '/';
      if (currentPathname.startsWith('/payments/')) return;

      if (typeof webengage === 'undefined' || !webengage?.track) return;

      const search = this.landingSearch || window.location.search || '';
      const params = new URLSearchParams(search.startsWith('?') ? search : search ? `?${search}` : '');

      // Only track real deeplink / campaign landings — not organic first navigations.
      const isShare =
        params.get('share_url') === 'true' ||
        params.get('url') === 'deeplinked';
      const hasDeeplinkSignal = !!(
        params.get('utm_source') ||
        params.get('utm_medium') ||
        params.get('utm_campaign') ||
        params.get('utm_content') ||
        params.get('utm_term') ||
        params.get('campaign_id') ||
        params.get('cid') ||
        params.get('we_campaign_id') ||
        params.get('deeplink') ||
        isShare
      );
      if (!hasDeeplinkSignal) {
        return;
      }

      sessionStorage.setItem(WE_DEEPLINK_OPENED_SESSION_KEY, '1');

      const landingPath = this.landingPathname || window.location.pathname || '/';
      const { target_screen, target_id } = mapDeeplinkTarget(landingPath);

      const hadVisit = localStorage.getItem(WE_SITE_VISITED_BEFORE_KEY);
      const is_first_session = !hadVisit;
      if (!hadVisit) localStorage.setItem(WE_SITE_VISITED_BEFORE_KEY, '1');

      const utmFallback = isShare ? 'share' : 'organic';
      const payload: Record<string, string | boolean> = {
        utm_source: params.get('utm_source') || utmFallback,
        utm_medium: params.get('utm_medium') || utmFallback,
        utm_campaign: params.get('utm_campaign') || utmFallback,
        deeplink_path: landingPath,
        deeplink_url:
          `${window.location.origin}${landingPath}${search.startsWith('?') || !search ? search : `?${search}`}` ||
          window.location.href ||
          '',
        target_screen,
        is_first_session,
      };

      const utmContent = params.get('utm_content');
      if (utmContent) payload.utm_content = utmContent;
      const utmTerm = params.get('utm_term');
      if (utmTerm) payload.utm_term = utmTerm;

      const campaignId =
        params.get('campaign_id') ||
        params.get('cid') ||
        params.get('we_campaign_id');
      if (campaignId) payload.campaign_id = campaignId;

      if (target_id) payload.target_id = target_id;

      console.log('WebEngage Deeplink Opened', payload);
      webengage.track('Deeplink Opened', payload);
    } catch {
      /* analytics must not break app */
    }
  }

  /**
   * WebEngage `Notification Permission Granted`
   * Fires once (persisted) when browser permission becomes granted.
   */
  private initNotificationPermissionListener(): void {
    try {
      if (typeof window === 'undefined') return;
      if (typeof Notification === 'undefined') return;
      const alreadyGranted = window.localStorage?.getItem(
        WE_NOTIF_GRANTED_TRACKED_KEY,
      );
      const alreadyDenied = window.localStorage?.getItem(
        WE_NOTIF_DENIED_TRACKED_KEY,
      );
      if (alreadyGranted && alreadyDenied) return;

      const trackFromPermission = (
        prev: string,
        next: string,
        source: string,
      ) => {
        const wasPrompt =
          prev === 'prompt' || prev === 'default' || prev === '';
        if (wasPrompt && next === 'granted') {
          this.trackNotificationPermissionGranted(source);
        } else if (wasPrompt && next === 'denied') {
          this.trackNotificationPermissionDenied(source);
        }
      };

      // Wrap Notification.requestPermission so webpush / FCM prompts are tracked.
      try {
        const originalRequest = Notification.requestPermission?.bind(Notification);
        if (typeof originalRequest === 'function' && !(Notification as any).__wePatched) {
          (Notification as any).__wePatched = true;
          Notification.requestPermission = ((
            deprecatedCallback?: any,
          ) => {
            const prev =
              Notification.permission === 'default'
                ? 'prompt'
                : Notification.permission;
            const result = originalRequest(deprecatedCallback);
            Promise.resolve(result)
              .then((permission: NotificationPermission) => {
                trackFromPermission(prev, permission, 'webpush_prompt');
                return permission;
              })
              .catch(() => {});
            return result;
          }) as typeof Notification.requestPermission;
        }
      } catch {
        /* ignore patch failures */
      }

      const navAny: any = navigator as any;
      if (!navAny?.permissions?.query) {
        // Fallback: poll briefly for permission changes after load.
        let lastPerm = Notification.permission;
        const pollId = window.setInterval(() => {
          const next = Notification.permission;
          if (next !== lastPerm) {
            trackFromPermission(
              lastPerm === 'default' ? 'prompt' : lastPerm,
              next,
              'permission_poll',
            );
            lastPerm = next;
          }
          if (next !== 'default') {
            window.clearInterval(pollId);
          }
        }, 1000);
        window.setTimeout(() => window.clearInterval(pollId), 60000);
        return;
      }

      navAny.permissions
        .query({ name: 'notifications' })
        .then((status: any) => {
          let lastState = status?.state || Notification.permission || 'prompt';
          const handler = () => {
            const next =
              status?.state ||
              (Notification.permission === 'default'
                ? 'prompt'
                : Notification.permission);
            trackFromPermission(lastState, next, 'contextual');
            lastState = next;
          };
          if (status) {
            status.onchange = handler;
          }
// NOTE: no immediate handler() call here — only fire on a real
          // permission *change* event, not on page-load snapshot of
          // pre-existing state.

          // Fallback: onchange is unreliable across browsers when permission
          // is changed by an external SDK (e.g. WebEngage's own push prompt).
          // Poll for up to 2 minutes after load as a backup trigger.
          let pollCount = 0;
          const pollInterval = setInterval(() => {
            pollCount++;
            if (pollCount > 24) {
              clearInterval(pollInterval);
              return;
            }
            const granted = window.localStorage?.getItem(WE_NOTIF_GRANTED_TRACKED_KEY);
            const denied = window.localStorage?.getItem(WE_NOTIF_DENIED_TRACKED_KEY);
            if (granted && denied) {
              clearInterval(pollInterval);
              return;
            }
            handler();
          }, 5000);
        })
        .catch(() => {
          /* ignore */
        });
    } catch {
      /* non-fatal */
    }
  }

  private trackNotificationPermissionGranted(_prompt_source: string): void {
    try {
      if (window.localStorage?.getItem(WE_NOTIF_GRANTED_TRACKED_KEY)) return;
      window.localStorage?.setItem(WE_NOTIF_GRANTED_TRACKED_KEY, '1');
    } catch {
      /* non-fatal */
    }
  }

  private trackNotificationPermissionDenied(_prompt_source: string): void {
    try {
      if (window.localStorage?.getItem(WE_NOTIF_DENIED_TRACKED_KEY)) return;
      window.localStorage?.setItem(WE_NOTIF_DENIED_TRACKED_KEY, '1');
    } catch {
      /* non-fatal */
    }
  }

  appConfigApi() {
    this.loadingService.showLoader();
    this.userService.appConfig(this.region).subscribe(
      (response) => {
        this.loadingService.hideLoader();
        this.isRegionSupported = response['data']?.is_region_supported;
        let ads_available;
        if (screen.width < 1200) {
          window.localStorage
            ? window.localStorage.setItem(
                'ad_slot1',
                JSON.stringify(
                  response?.data?.params_hash2?.config_params?.mweb_ads,
                ),
              )
            : '';
        } else {
          window.localStorage
            ? window.localStorage.setItem(
                'ad_slot1',
                JSON.stringify(
                  response?.data?.params_hash2?.config_params?.web_ads,
                ),
              )
            : '';
        }
        window.localStorage
          ? window.localStorage.setItem(
              'banner_ads_available',
              JSON.stringify(response?.data?.banner_ads_available),
            )
          : '';
      },
      (error) => {
        //this.loadingIndicator = false;
        this.loadingService.hideLoader();
        //console.log(error);
      },
    );
  }
}
